/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import QRCode from 'react-qr-code';
import { isMobile } from 'react-device-detect';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { WalletScreens } from '../../constants';
import CurrencyIcon from '../../../../components/CurrencyIcon';
import { useLedgerFilteredCurrencies } from '../../../../utilities/sharedHooks/ledgerWallets';
import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import { copyToClipboard } from '../../../../utilities/helpers';
import { currencies, currenciesDefault } from '../../../../utilities/constants';

const Receive = ({ openScreen, options }) => {
  const { t: translate } = useTranslation();

  const collateralWallet = options ? options.wallet : null;

  const currency = collateralWallet?.coin.toUpperCase();

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const walletAddressObj = useMemo(() => {
    const addressArr = collateralWallet?.address?.split('?dt=');

    const [address, memo] = addressArr;

    return { address, memo };
  }, [collateralWallet]);

  const renderTitle = () => (
    <b className="text-f5 text-dark me-auto">
      <CurrencyIcon
        currencyCode={currency}
        className="me-2"
        style={{
          width: '1.6rem',
          height: '1.6rem',
          ...(currency?.toUpperCase() === currencies.XRP.icon
            ? {
                backgroundColor: 'white',
                padding: '1px',
                borderRadius: '20px',
              }
            : {}),
        }}
      />
      {translate('deposit_wallet_receive').replace('%s', currency.toUpperCase())}
    </b>
  );

  const renderFooter = () => (
    <button
      className="btn btn-primary"
      type="button"
      onClick={() => copyToClipboard(walletAddressObj?.address)}>
      {translate('copy_to_clipboard_btn_label')}
    </button>
  );

  const currencyDisplayWithToken = useMemo(() => {
    let displayCurrency = currency?.toUpperCase();
    if (supportedCurrencies) {
      if (
        supportedCurrencies[currency?.toLowerCase()] &&
        supportedCurrencies[currency?.toLowerCase()]?.is_erc20_token
      ) {
        displayCurrency = `ERC20 ${currency.toUpperCase()}`;
      }
    }
    return displayCurrency;
  }, [supportedCurrencies, currency]);

  const currencyDisplayWithFullName = useMemo(() => {
    let displayCurrency = currenciesDefault[currency.toUpperCase()]?.fullName;
    if (supportedCurrencies) {
      if (
        supportedCurrencies[currency?.toLowerCase()] &&
        supportedCurrencies[currency?.toLowerCase()]?.is_erc20_token
      ) {
        displayCurrency = 'Ethereum';
      }
    }
    return displayCurrency;
  }, [supportedCurrencies, currency]);

  const withBackButton = useMemo(() => {
    if (supportedCurrencies) {
      return (
        supportedCurrencies[currency?.toLowerCase()]?.secure_funding_enabled || options?.withBackBtn
      );
    }
    return false;
  }, [supportedCurrencies, options]);

  return (
    <ModalBodyLayout
      withBack={withBackButton}
      isScrollable={false}
      onPrevClick={() =>
        options?.withBackBtn
          ? openScreen(WalletScreens.DEPOSIT_WALLET_SELECTION, { ...options })
          : openScreen(WalletScreens.LEDGER_DEPOSIT, { ...options })
      }
      renderTitle={renderTitle}
      renderFooter={walletAddressObj?.memo ? () => {} : renderFooter}>
      <div className="mt-4 w-100">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <div className="d-flex flex-column me-2">
            <p className="text-f6 text-shadow mt-3">
              {translate('address_to_receive_crypto_label').replace('__CURRENCY__', currency)}
            </p>
            <p
              className="text-f5 text-dark"
              style={{
                wordBreak: 'break-word',
                width: walletAddressObj?.memo ? '' : !isMobile && '25rem',
              }}>
              {walletAddressObj?.address}
            </p>
          </div>
          {walletAddressObj?.memo && (
            <div className="align-self-end px-1 pt-1 bg-copy rounded-max">
              <i
                onKeyDown={null}
                tabIndex={-1}
                role="button"
                aria-label="Copy"
                className="icon-copy-other text-primary text-f4 cursor-pointer"
                onClick={() => copyToClipboard(walletAddressObj?.address)}
              />
            </div>
          )}
        </div>
      </div>

      {walletAddressObj?.memo && (
        <div className="mt-2 w-100">
          <div className="d-flex flex-row justify-content-between  align-items-center">
            <div className="d-flex flex-column">
              <p className="text-f6 text-shadow upper-case mt-3">
                {translate('receive_memo_field_label')}
              </p>
              <p className="text-f5 text-dark">{walletAddressObj?.memo}</p>
            </div>
            <div className="align-self-end px-1 pt-1 bg-copy rounded-max">
              <i
                onKeyDown={null}
                tabIndex={-1}
                role="button"
                aria-label="Copy"
                className="icon-copy-other text-primary text-f4 cursor-pointer"
                onClick={() => copyToClipboard(walletAddressObj?.memo)}
              />
            </div>
          </div>
        </div>
      )}
      <div
        className={`d-flex flex-column justify-content-around gap-3 align-items-center h-75 ${
          walletAddressObj?.memo ? 'mt-4' : 'mt-5'
        }`}>
        <div className="bg-white p-2">
          <QRCode value={walletAddressObj?.address} size={200} level="M" bgColor="#ffffff" />
        </div>

        <div className="alert alert-info" role="alert">
          <i className="icon icon-info-icon" />
          {walletAddressObj?.memo ? (
            <span className="content">
              <p>
                <Trans i18nKey="receive_with_memo_note" components={[<b key="content-1" />]} />
              </p>
              <p>
                <Trans
                  i18nKey="receive_with_memo_note_1"
                  components={[<b key="content-1" />, <b key="content-2" />]}
                />
              </p>
              <p>
                <Trans i18nKey="receive_with_memo_note_2" />
              </p>
            </span>
          ) : (
            <span className="content">
              <Trans
                i18nKey="receive_with_crypto_note"
                values={{
                  currency: currencyDisplayWithToken,
                  full_name: currencyDisplayWithFullName,
                }}
                components={[<b key="content-1" />, <b key="content-2" />]}
              />
            </span>
          )}
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default Receive;

Receive.propTypes = {
  openScreen: PropTypes.func.isRequired,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

Receive.defaultProps = {
  options: null,
};
